import { format, setDay } from 'date-fns';
import type { Locale } from 'date-fns';
import type { OpeningHoursPeriod } from '../api/places.api';

export interface WeekdayHours {
  /** Localised weekday name, week starting on Monday. */
  day: string;
  /** Localised opening ranges, or null when the place is closed that day. */
  hours: string | null;
  isToday: boolean;
}

/**
 * Google returns opening hours twice: as `weekdayDescriptions` (prose, in
 * whatever language the request asked for) and as `regularOpeningHours.periods`
 * (day/hour/minute numbers). The prose was being shown verbatim, so the card
 * read "Monday 10:00 AM – 6:30 PM" with the rest of the UI in Spanish.
 *
 * Building the schedule from the numeric periods keeps it language-agnostic:
 * the same cached Place Details entry renders correctly in every locale, with
 * no extra Google call and no language dimension on the shared cache.
 */
export function formatWeeklyHours(periods: OpeningHoursPeriod[], locale: Locale): WeekdayHours[] {
  const todayIdx = (new Date().getDay() + 6) % 7; // Monday-first
  const fmtTime = (h: number, m: number) => {
    const d = new Date();
    d.setHours(h, m, 0, 0);
    return format(d, 'p', { locale });
  };

  return Array.from({ length: 7 }, (_, i) => {
    const googleDay = (i + 1) % 7; // Google weeks start on Sunday (0)
    const ranges = periods
      .filter(p => p.open.day === googleDay)
      .map(p => (p.close
        ? `${fmtTime(p.open.hour, p.open.minute)} – ${fmtTime(p.close.hour, p.close.minute)}`
        : fmtTime(p.open.hour, p.open.minute)));

    return {
      day: format(setDay(new Date(), googleDay), 'EEEE', { locale }),
      hours: ranges.length ? ranges.join(', ') : null,
      isToday: i === todayIdx,
    };
  });
}
